import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  catchError,
  EMPTY,
  expand,
  finalize,
  last,
  map,
  of,
  scan,
  Subject,
  switchMap,
  takeUntil
} from 'rxjs';
import { FileService } from 'src/app/services/api/file.service';
import { IncidentService } from 'src/app/services/api/incident.service';
import { DialogIncidentHistoryComponent } from '../dialog-incident-history/dialog-incident-history.component';

interface PreOperativeIncident {
  incidentDate: string;
  approvedDate: string;
  deadline: string;
  fileNumber: string;
  fileClient: string;
  incidentReason: string;
  destination: string;
  incidentStatus: string;
  description: string;
  ds: string;
  nameClient: string;
  historial: Array<{
    timestamp: string;
    updatedBy: string;
    incidentStatus: string;
  }>;
}
const INCIDENCIA_PRE_OPERATIVA = 'INCIDENCIA_PRE_OPERATIVA' as const;

@Component({
  selector: 'app-pre-operativas',
  templateUrl: './pre-operativas.component.html',
  styleUrls: ['./pre-operativas.component.css']
})
export class PreOperativasComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  displayedColumns: string[] = [
    'incidentDate',
    'approvedDate',
    'deadline',
    'fileNumber',
    'nameClient',
    'incidentReason',
    'ds',
    'destination',
    'description',
    'incidentStatus',
    'details'
  ];
  preOperationalDataSource = new MatTableDataSource<PreOperativeIncident>([]);

  isLoading = true;
  loadingMessage = 'Cargando incidencias pre-operativas...';
  hasError = false;
  showFilters = false;

  searchQuery = '';
  filterDate: Date | null = null;
  filterReason = '';
  filterDestination = '';
  filterStatus = '';

  readonly INCIDENT_STATUS = {
    PENDING: 'PENDING',
    RESOLVED: 'RESOLVED',
    APPROVED: 'APPROVED',
    NOT_APPROVED: 'NOT_APPROVED'
  } as const;

  readonly reasonOptions = [
    'Cotización',
    'Datos de vuelos',
    'Datos de pasajeros',
    'Itinerario',
    'Otro'
  ];
  readonly destinationOptions = ['Lima', 'Cusco', 'Huaraz', 'Otros'];
  readonly statusOptions = [
    { value: this.INCIDENT_STATUS.PENDING, label: 'Creado' },
    { value: this.INCIDENT_STATUS.RESOLVED, label: 'Resuelto por DS' },
    { value: this.INCIDENT_STATUS.NOT_APPROVED, label: 'No Aprobado' },
    { value: this.INCIDENT_STATUS.APPROVED, label: 'Aprobado' }
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private readonly incidentService: IncidentService,
    private readonly fileService: FileService,
    private dialog: MatDialog
  ) {}

  openIncidentsHistorial(element: PreOperativeIncident) {
    this.dialog.open(DialogIncidentHistoryComponent, {
      width: '600px',
      data: {
        incidentType: INCIDENCIA_PRE_OPERATIVA,
        historial: element.historial || []
      }
    });
  }

  ngOnInit(): void {
    this.setupFilterPredicate();
    this.getAllPreOperativas();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupFilterPredicate() {
    this.preOperationalDataSource.filterPredicate = (data, filter: string) => {
      const searchTerms = JSON.parse(filter);

      // 1. Text Search
      const textMatch =
        !searchTerms.query ||
        JSON.stringify(data)
          .toLowerCase()
          .includes(searchTerms.query.toLowerCase());

      // 2. Date Match
      let dateMatch = true;
      if (searchTerms.date) {
        const itemDate = new Date(data.incidentDate);
        const filterD = new Date(searchTerms.date);
        dateMatch = itemDate.toDateString() === filterD.toDateString();
      }

      // 3. Reason Match
      let reasonMatch = true;
      if (searchTerms.reason) {
        if (searchTerms.reason === 'Otro') {
          reasonMatch = !this.reasonOptions
            .filter((o) => o !== 'Otro')
            .includes(data.incidentReason);
        } else {
          reasonMatch = data.incidentReason === searchTerms.reason;
        }
      }

      // 4. Destination Match
      let destMatch = true;
      if (searchTerms.destination) {
        if (searchTerms.destination === 'Otros') {
          destMatch = !this.destinationOptions
            .filter((o) => o !== 'Otros')
            .includes(data.destination);
        } else {
          destMatch = data.destination === searchTerms.destination;
        }
      }

      // 5. Status Match
      let statusMatch = true;
      if (searchTerms.status) {
        statusMatch = data.incidentStatus === searchTerms.status;
      }

      return textMatch && dateMatch && reasonMatch && destMatch && statusMatch;
    };
  }

  applyFilters() {
    const filterObj = {
      query: this.searchQuery,
      date: this.filterDate,
      reason: this.filterReason,
      destination: this.filterDestination,
      status: this.filterStatus
    };
    this.preOperationalDataSource.filter = JSON.stringify(filterObj);
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
    if (!this.showFilters) {
      this.filterDate = null;
      this.filterReason = '';
      this.filterDestination = '';
      this.filterStatus = '';
      this.applyFilters();
    }
  }

  refreshData() {
    this.getAllPreOperativas();
  }

  private fetchAllPreOperativeIncidents() {
    return this.incidentService
      .findAllPreOperationalIncidents({
        startEvaluatedKeyId: undefined
      })
      .pipe(
        expand((response) => {
          const nextStartEvaluatedKeyId =
            response.findAllIncidenciasPreOperativas?.lastEvaluatedKeyId ??
            undefined;

          if (!nextStartEvaluatedKeyId) {
            return EMPTY;
          }

          return this.incidentService.findAllPreOperationalIncidents({
            startEvaluatedKeyId: nextStartEvaluatedKeyId
          });
        }),
        map(
          (response) =>
            response.findAllIncidenciasPreOperativas?.preOpIncidents ?? []
        ),
        scan((allPreOpIncidents, pagePreOpIncidents) => {
          const nextAllPreOpIncidents = [
            ...allPreOpIncidents,
            ...pagePreOpIncidents
          ];
          this.loadingMessage = `Cargando incidencias pre-operativas... ${nextAllPreOpIncidents.length} registros cargados`;
          return nextAllPreOpIncidents;
        }, [] as any[]),
        last()
      );
  }

  getAllPreOperativas(): void {
    this.isLoading = true;
    this.hasError = false;
    this.preOperationalDataSource.data = [];
    this.loadingMessage = 'Cargando incidencias pre-operativas...';

    this.fetchAllPreOperativeIncidents()
      .pipe(
        switchMap((findAllIncidenciasPreOperativas) => {
          if (!findAllIncidenciasPreOperativas.length) return of([]);

          const mappedData = findAllIncidenciasPreOperativas.map(
            (item: any) => {
              const preOpIncidents: PreOperativeIncident = {
                ...item
              };
              const approvedIncident = item.historial
                ?.sort(
                  (a: any, b: any) =>
                    new Date(b.timestamp).getTime() -
                    new Date(a.timestamp).getTime()
                )
                .find(
                  (historialItem: any) =>
                    historialItem.incidentStatus ===
                    this.INCIDENT_STATUS.APPROVED
                );

              if (approvedIncident) {
                preOpIncidents.approvedDate = approvedIncident.timestamp;
              }

              return preOpIncidents;
            }
          );

          const allItineraryIds = Array.from(
            new Set(mappedData.flatMap((d: any) => d.itineraryId))
          ) as string[];

          console.log({ mappedData });
          if (allItineraryIds.length === 0) {
            return of(mappedData);
          }

          return this.getItinerariesByIds(allItineraryIds).pipe(
            map((itineraries: any[]) => {
              return mappedData.map((d: any) => {
                const matchedItinerary = itineraries.find(
                  (i) => i.id === d.itineraryId
                );
                return {
                  ...d,
                  ds: matchedItinerary?.usuario || null,
                  fileNumber:
                    `${matchedItinerary?.fileNumber}${matchedItinerary?.fileExtend || ''}` ||
                    null,
                  nameClient: matchedItinerary?.nameClient || null
                };
              });
            })
          );
        }),
        catchError(() => {
          this.hasError = true;
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (mappedPreOperationalIncidents) => {
          if (!this.hasError) {
            this.preOperationalDataSource.data = mappedPreOperationalIncidents;
            this.preOperationalDataSource.paginator = this.paginator;
            this.preOperationalDataSource.sort = this.sort;
            this.applyFilters();
          }
        }
      });
  }

  getItinerariesByIds(itineraryIds: string[]) {
    return this.fileService
      .getItineraryByIds({
        ids: itineraryIds,
        projectionExpression: 'id, fileNumber, fileExtend, nameClient, usuario'
      })
      .pipe(
        map((response: any) => {
          return response.getItineraryByIds || [];
        })
      );
  }
}
